import React, { useCallback, useEffect, useState } from 'react';
import { convertMarkdownToHtml } from 'src/utils';

interface AssistantMessageProps {
  content: string;
  onAnimationUpdate?: () => void;
}

export const AssistantMessage = ({ content, onAnimationUpdate }: AssistantMessageProps) => {
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(true);

  // Reset animation when content changes
  useEffect(() => {
    setIndex(0);
    setFinished(false);
    setShouldAnimate(true);
  }, [content]);

  const setRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      // Check if this is the last assistant message
      const parent = node.closest('.copilotKitMessages');
      if (parent) {
        const allAssistantMessages = Array.from(parent.querySelectorAll('.assistant-message'));
        const currentMessage = node.closest('.assistant-message');
        const isLast = currentMessage === allAssistantMessages[allAssistantMessages.length - 1];
        
        if (!isLast) {
          // If not last, show full content immediately
          setShouldAnimate(false);
          setIndex(content.length);
          setFinished(true);
        } else {
          // If last, enable animation
          setShouldAnimate(true);
        }
      }
    }
  }, [content.length]);

  useEffect(() => {
    if (!content || !shouldAnimate) return;

    if (index < content.length) {
      const timeout = setTimeout(() => {
        setIndex((prev) => prev + 1);
      }, Math.random() * 1 + 2);
      return () => clearTimeout(timeout);
    } else {
      setFinished(true);
    }
  }, [index, content, shouldAnimate]);

  // Call onAnimationUpdate during animation - on newlines or every ~50 chars
  useEffect(() => {
    if (shouldAnimate && index > 0 && onAnimationUpdate) {
      const currentChar = content[index - 1];
      // Scroll on newlines or every 50 characters
      if (currentChar === '\n' || index % 50 === 0 || index === content.length) {
        onAnimationUpdate();
      }
    }
  }, [index, shouldAnimate, onAnimationUpdate, content]);

  const currentContent = content.slice(0, index);

  return (
    <div className="flex flex-col assistant-message pr-2" ref={setRef}>
      <div className="flex items-start">
        <div className="text-text rounded-xl first-message">
          <div
            className="copilotKitMarkdownElement"
            dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(currentContent) }}
          />
        </div>
      </div>
    </div>
  );
};
